/**
 * VH-83: Ingredient row with name autocomplete + amount/unit/notes/macros.
 *
 * The autocomplete intentionally stays lightweight: the foods table is large,
 * so we let the user type a name. If the food is unknown, on save the backend
 * stores the snapshot (food_id stays null). When the user explicitly picks a
 * matching food via the dropdown we set food_id; otherwise it's
 * "create-on-the-fly".
 *
 * For the foundational VH-83 capture flow we don't need a full live search —
 * the field is editable as a plain text input, which the backend already
 * tolerates (food_name_snapshot is required, food_id is optional). VH-84 will
 * add live food search on top of this same component.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TrashIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

export default function IngredientPicker({
  value,
  onChange,
  onRemove,
  index,
}) {
  const { t } = useTranslation()
  const [showAdvanced, setShowAdvanced] = useState(false)

  const update = (patch) => onChange?.({ ...value, ...patch })

  return (
    <div
      data-testid={`ingredient-row-${index}`}
      className="border border-gray-200 rounded-lg p-3 space-y-2 bg-surface-container"
    >
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-12 sm:col-span-5">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('recipes.ingredients.name')}
          </label>
          <input
            type="text"
            value={value.food_name_snapshot || ''}
            onChange={(e) => update({ food_name_snapshot: e.target.value })}
            placeholder={t('recipes.ingredients.namePlaceholder')}
            className="form-input w-full"
            data-testid={`ingredient-name-${index}`}
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('recipes.ingredients.amount')}
          </label>
          {/* VH-263: step="0.1" made the browser refuse any amount that was not a
              multiple of a tenth, so a scaled recipe ("0.114 tsp") could not be saved
              until every such row had been rounded by hand. The rounding is done for
              the user now — on the value, not by blocking the form. */}
          <input
            type="number"
            min="0"
            step="any"
            value={value.amount ?? ''}
            onChange={(e) =>
              update({
                amount: e.target.value === '' ? '' : parseFloat(e.target.value),
              })
            }
            className="form-input w-full"
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('recipes.ingredients.unit')}
          </label>
          <input
            type="text"
            value={value.unit || ''}
            onChange={(e) => update({ unit: e.target.value })}
            placeholder="g"
            className="form-input w-full"
          />
        </div>
        <div className="col-span-12 sm:col-span-1 flex items-end justify-end">
          <button
            type="button"
            onClick={() => onRemove?.()}
            className="p-2 text-gray-400 hover:text-red-600"
            title={t('recipes.ingredients.remove')}
            aria-label={t('recipes.ingredients.remove')}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div>
        <input
          type="text"
          value={value.notes || ''}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder={t('recipes.ingredients.notes')}
          className="form-input w-full text-sm"
        />
      </div>

      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
      >
        {showAdvanced ? (
          <ChevronUpIcon className="h-3 w-3" />
        ) : (
          <ChevronDownIcon className="h-3 w-3" />
        )}
        {t('recipes.ingredients.advanced')}
      </button>

      {showAdvanced && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {[
            ['kcal_snapshot', 'kcal'],
            ['protein_g_snapshot', 'protein'],
            ['carbs_g_snapshot', 'carbs'],
            ['fat_g_snapshot', 'fat'],
            ['fiber_g_snapshot', 'fiber'],
            ['sugars_g_snapshot', 'sugars'],
          ].map(([field, labelKey]) => (
            <div key={field}>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
                {t(`recipes.ingredients.${labelKey}`)}
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={value[field] ?? ''}
                onChange={(e) =>
                  update({
                    [field]:
                      e.target.value === ''
                        ? null
                        : parseFloat(e.target.value),
                  })
                }
                className="form-input w-full text-xs"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
